import { useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { Search } from 'lucide-react'
import type { SearchResult, ParsedFilter, FacetHighlight } from '@/types/search'
import { FacetHighlightChip } from '@/components/Shared/FacetHighlightChip'
import { HyrinWordmark } from '@/components/Shared/HyrinWordmark'

interface SearchIndexProps {
  query?: string
  results: SearchResult[]
  parsedFilters: ParsedFilter[]
}

const EXAMPLE_QUERIES = [
  'senior RAG engineer, shipped in production',
  'inference engineer with CUDA, prev big tech',
  'ML researcher turned engineer, published papers',
  'founding ML engineer, fast-ai community',
]

interface FloatingCard extends FacetHighlight {
  initials: string
  name: string
  reason: string
  rotate: string
  x: string
  y: string
}

const FLOATING_CARDS: FloatingCard[] = [
  { initials: 'MK', name: 'Maya K.', facetType: 'Technical Depth', evidenceState: 'Strong Evidence', reason: 'fast-rag 3.1k★ · NeurIPS', rotate: '-rotate-[6deg]', x: 'left-[6%]', y: 'top-[20%]' },
  { initials: 'DR', name: 'Devansh R.', facetType: 'Production Reliability', evidenceState: 'Some Evidence', reason: 'vllm-serve · CUDA', rotate: 'rotate-[4deg]', x: 'right-[7%]', y: 'top-[28%]' },
  { initials: 'JL', name: 'Jordan L.', facetType: 'Shipping Ability', evidenceState: 'Some Evidence', reason: 'rag-eval-kit 1.4k★', rotate: '-rotate-[3deg]', x: 'left-[11%]', y: 'top-[52%]' },
  { initials: 'PR', name: 'Priya R.', facetType: 'AI Tool Fluency', evidenceState: 'Emerging Signal', reason: 'LangChain contrib', rotate: 'rotate-[5deg]', x: 'right-[12%]', y: 'top-[55%]' },
]

export default function SearchIndex({ query, results, parsedFilters }: SearchIndexProps) {
  const [inputValue, setInputValue] = useState(query ?? '')
  const hasResults = !!query

  function handleSearch(q: string) {
    if (!q.trim()) { return }
    router.get('/search', { query: q }, { preserveState: false })
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    handleSearch(inputValue)
  }

  return (
    <>
      <Head title={query ? `"${query}" — Hyrin Search` : 'Search individuals — Hyrin'} />

      <div className="flex min-h-screen flex-col bg-muted">
        {/* Header */}
        <header className="flex h-16 items-center justify-between border-b border-border px-10">
          <a href="/" className="flex items-center gap-2">
            <HyrinWordmark />
          </a>
          <div className="flex items-center gap-5">
            <a href="#" className="font-sans text-[13px] text-muted-foreground hover:text-foreground">
              Saved searches
            </a>
            <div className="flex size-[30px] items-center justify-center rounded-full bg-foreground font-sans text-[12px] font-bold text-background">
              Y
            </div>
          </div>
        </header>

        {hasResults ? (
          /* ─── Results state ─── */
          <div className="flex flex-1 flex-col">
            {/* Condensed search bar */}
            <div className="border-b border-border bg-background px-10 py-4">
              <form onSubmit={handleSubmit}>
                <div className="flex h-[46px] max-w-[680px] items-center gap-3 rounded-[10px] border border-border bg-muted px-4">
                  <Search size={15} className="shrink-0 text-muted-foreground" />
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    className="flex-1 bg-transparent font-sans text-[14px] text-foreground outline-none placeholder:text-muted-foreground"
                    placeholder="Describe the individual you need…"
                    aria-label="Search individuals"
                  />
                  {inputValue && (
                    <button
                      type="submit"
                      className="shrink-0 rounded-[7px] bg-foreground px-3 py-[5px] font-sans text-[12px] font-semibold text-background"
                    >
                      Search
                    </button>
                  )}
                </div>
              </form>

              {/* Parsed filter chips */}
              {parsedFilters.length > 0 && (
                <div className="mt-3 flex items-center gap-[8px]">
                  <span className="font-mono text-[11px] text-muted-foreground">parsed →</span>
                  {parsedFilters.map((f) => (
                    <span
                      key={`${f.label}-${f.value}`}
                      className="inline-flex items-center gap-[6px] rounded-full border border-border bg-background px-[10px] py-[5px] font-mono text-[11px] text-foreground"
                    >
                      <span className="size-[5px] rounded-full bg-primary" />
                      {f.value}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Results list */}
            <main className="flex-1 px-10 py-6">
              <p className="mb-4 font-mono text-[11px] text-muted-foreground">
                {results.length} result{results.length !== 1 ? 's' : ''}
              </p>
              <div className="flex max-w-[680px] flex-col gap-[10px]">
                {results.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center gap-4 rounded-card border border-border bg-background px-5 py-[16px]"
                  >
                    <div className="flex size-[44px] shrink-0 items-center justify-center rounded-full bg-primary font-heading text-[16px] font-bold text-primary-foreground">
                      {r.initials}
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <div className="flex items-center gap-[7px]">
                        <span className="font-sans text-[15px] font-semibold text-foreground">
                          {r.name}
                        </span>
                        {r.verified && (
                          <span className="font-mono text-[11px] font-semibold text-primary">✓</span>
                        )}
                        <span className="font-sans text-[12px] text-muted-foreground">{r.location}</span>
                        <span className="font-mono text-[11px] text-muted-foreground">{r.builderType}</span>
                      </div>
                      <p className="mt-[3px] truncate font-mono text-[12px] text-muted-foreground">
                        {r.matchReason}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-[5px]">
                      {r.facetHighlights.map((h) => (
                        <FacetHighlightChip key={h.facetType} highlight={h} />
                      ))}
                    </div>
                    <button className="ml-2 h-[38px] shrink-0 rounded-[9px] border border-foreground bg-background px-4 font-sans text-[13px] font-semibold text-foreground hover:bg-muted">
                      Contact
                    </button>
                  </div>
                ))}
              </div>
            </main>
          </div>
        ) : (
          /* ─── Empty state ─── */
          <div className="relative flex flex-1 flex-col items-center justify-center overflow-hidden px-6 py-12">
            {/* Floating background cards */}
            {FLOATING_CARDS.map((card) => (
              <div
                key={card.initials}
                className={`pointer-events-none absolute ${card.x} ${card.y} ${card.rotate} w-[200px] rounded-[12px] border border-border bg-background px-4 py-3 shadow-sm opacity-60`}
              >
                <div className="flex items-center gap-[10px]">
                  <div className="flex size-[30px] shrink-0 items-center justify-center rounded-full bg-primary font-heading text-[11px] font-bold text-primary-foreground">
                    {card.initials}
                  </div>
                  <div>
                    <p className="font-sans text-[12px] font-semibold text-foreground">{card.name}</p>
                    <p className="font-mono text-[10px] text-muted-foreground">{card.reason}</p>
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-mono text-[10px] text-muted-foreground">{card.evidenceState.toLowerCase()}</span>
                  <FacetHighlightChip highlight={card} />
                </div>
              </div>
            ))}

            {/* Center search */}
            <div className="relative z-10 flex w-full max-w-[580px] flex-col items-center">
              <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Search individuals
              </p>
              <h1 className="mb-7 text-center font-heading text-[34px] font-bold leading-[1.1] tracking-[-0.025em] text-foreground">
                Find individuals who can prove it.
              </h1>

              <form onSubmit={handleSubmit} className="w-full">
                <div className="flex h-[56px] w-full items-center gap-3 rounded-[14px] border border-border bg-background px-5 shadow-[0_4px_24px_rgba(20,40,32,0.08)]">
                  <Search size={17} className="shrink-0 text-muted-foreground" />
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    className="flex-1 bg-transparent font-sans text-[16px] text-foreground outline-none placeholder:text-muted-foreground"
                    placeholder="Describe the individual you need…"
                    aria-label="Search individuals"
                    autoFocus
                  />
                  <span className="font-mono text-[11px] text-muted-foreground">↵ to search</span>
                </div>
              </form>

              {/* Example pills */}
              <div className="mt-5 flex flex-wrap justify-center gap-[8px]">
                {EXAMPLE_QUERIES.map((q) => (
                  <button
                    key={q}
                    onClick={() => {
                      setInputValue(q)
                      handleSearch(q)
                    }}
                    className="rounded-full border border-border bg-background px-[13px] py-[8px] font-sans text-[13px] text-muted-foreground hover:border-foreground hover:text-foreground"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
